import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, Rocket, X, Sparkles } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";

export function useOnboarding() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/onboarding").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const act = useCallback(async (action) => {
    const { data } = await api.post(`/onboarding/${action}`);
    setData(data);
    return data;
  }, []);
  return { data, reload: load, act };
}

// Full-screen guided walkthrough shown on first login until seen/complete.
export function OnboardingWizard() {
  const { data, act } = useOnboarding();
  const navigate = useNavigate();
  if (!data || data.wizard_seen || data.complete || data.dismissed) return null;

  const done = data.steps.filter((s) => s.done).length;
  const goTo = async (to) => { await act("seen-wizard"); navigate(to); };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-testid="onboarding-wizard">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="relative bg-[#4338CA] px-7 py-7 text-white">
          <button onClick={() => act("seen-wizard")} data-testid="wizard-skip" className="absolute right-4 top-4 rounded-md p-1 text-white/70 transition-colors hover:bg-white/15 hover:text-white">
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-indigo-200"><Rocket className="h-4 w-4" /> Getting started</div>
          <h2 className="mt-2 font-head text-2xl font-bold">Welcome to Servantage 👋</h2>
          <p className="mt-1 text-sm text-indigo-100">Five quick steps to start capturing verified service feedback. You can do these in any order.</p>
        </div>
        <div className="p-5">
          <div className="space-y-2">
            {data.steps.map((s, i) => (
              <button key={s.key} onClick={() => goTo(s.to)} data-testid={`wizard-step-${s.key}`}
                className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${s.done ? "border-emerald-100 bg-emerald-50/50" : "border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/40"}`}>
                {s.done ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" /> : <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-500">{i + 1}</span>}
                <span className={`flex-1 text-sm font-semibold ${s.done ? "text-slate-400 line-through" : "text-slate-800"}`}>{s.label}</span>
                {!s.done && <ArrowRight className="h-4 w-4 text-slate-300" />}
              </button>
            ))}
          </div>
          <div className="mt-5 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400">{done} of {data.steps.length} done</span>
            <Button onClick={() => act("seen-wizard")} data-testid="wizard-start" className="bg-[#4338CA] hover:bg-[#3730A3]">
              Let's go <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Dismissible progress card for the dashboard — remains until complete or dismissed.
export function OnboardingChecklist() {
  const { data, act } = useOnboarding();
  const navigate = useNavigate();
  if (!data || data.complete || data.dismissed || !data.steps.length) return null;

  const done = data.steps.filter((s) => s.done).length;
  const pct = Math.round((done / data.steps.length) * 100);

  return (
    <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/70 to-white p-5" data-testid="onboarding-checklist">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-indigo-500"><Sparkles className="h-4 w-4" /> Finish setting up</div>
          <h3 className="mt-1 font-head text-lg font-bold text-slate-900">You're {pct}% set up</h3>
        </div>
        <button onClick={() => act("dismiss")} data-testid="checklist-dismiss" className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" title="Hide">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-indigo-100">
        <div className="h-full rounded-full bg-[#4338CA] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {data.steps.map((s) => (
          <div key={s.key} data-testid={`checklist-step-${s.key}`}
            className={`flex items-center gap-2.5 rounded-lg border p-2.5 ${s.done ? "border-emerald-100 bg-emerald-50/50" : "border-slate-200 bg-white"}`}>
            {s.done ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" /> : <Circle className="h-4 w-4 shrink-0 text-slate-300" />}
            <span className={`flex-1 text-sm font-medium ${s.done ? "text-slate-400 line-through" : "text-slate-700"}`}>{s.label}</span>
            {!s.done && (
              <div className="flex items-center gap-1">
                {s.key === "qr" && <button onClick={() => act("step-qr")} data-testid="checklist-qr-done" className="text-[11px] font-bold text-slate-400 hover:text-emerald-600">Mark done</button>}
                <button onClick={() => navigate(s.to)} data-testid={`checklist-go-${s.key}`} className="rounded-md p-1 text-indigo-500 hover:bg-indigo-50"><ArrowRight className="h-4 w-4" /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
